import {
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Box,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Skeleton,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Text,
  Flex,
} from '@chakra-ui/react';
import * as _ from 'lodash';
import React from 'react';

import {
  error as commonError,
  finleyAssert,
} from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import {
  AssignmentBorrowingBaseReportGQLType,
  AssignmentReceivableGQLType,
  Maybe,
} from 'types/dashboard-core-gqlTypes.generated';

import { groupReceivablesByCreditFacilityId } from './utils';
import { colors } from 'packages/common-frontend/colors';
import { IconButton } from 'packages/common-frontend/components/IconButton';
import {
  numberToDollarFormattedRounded,
  numberToPercentageLabel,
} from 'packages/common-frontend/utils';
import { useGetCreditAgreementBorrowerEntityNameQuery } from 'packages/creditAgreements/requests/requests.gql.generated';
import { DownloadExtendedResponseTapePreviewButton } from 'packages/loanProduct/ui/DownloadExtendedResponseTapePreviewButton';
import { EmptyDownloadAssignmentPreviewButton } from 'packages/loanProduct/ui/DownloadPreviewLoadingButtons';
import { EmailServicerReportPreviewButton } from 'packages/loanProduct/ui/EmailServicerReportPreviewButton';
import { FiList } from 'react-icons/fi';

const InfoBoxRow = ({
  label,
  before,
  after,
  preview,
}: {
  label: string;
  before: Maybe<string>;
  after: Maybe<string>;
  preview: boolean;
}) => {
  return (
    <Tr>
      <Td>{label}</Td>
      <Td>{before}</Td>
      <Td color={preview ? colors.blue[500] : 'black'}>{after}</Td>
    </Tr>
  );
};

const AssignmentSummaryBorrowingBaseInfoBox = ({
  title,
  creditFacilityId,
  previouslyAssignedReceivables,
  proposedAssignmentReceivables,
  fileVersion,
  reportingDate,
  children,
}: {
  title: string;
  creditFacilityId: string;
  previouslyAssignedReceivables?: AssignmentReceivableGQLType[];
  proposedAssignmentReceivables?: AssignmentReceivableGQLType[];
  fileVersion: number;
  reportingDate: Util.FullDateStr;
  children: React.ReactNode;
}) => {
  const proposedDownloadButtonLabel =
    'Proposed Extended Receivable Response Tape Preview';
  const proposedAndAssignedButtonLabel =
    'Extended Receivable Response Tape Preview';
  const proposedExtendedResponseTapeFilePrefix =
    'proposed-extended-receivable-response-tape-preview';
  const proposedAndAssignedExtendedResponseTapePrefix =
    'extended-receivable-response-tape-preview';

  return (
    <Box borderWidth="1px 1px 1px 1px" marginBottom="30px">
      <Box
        background={colors.tableHeader}
        w="100%"
        p={4}
        color="black"
        borderBottomWidth="1px"
      >
        {title}
      </Box>
      <Box marginTop="20px">
        <Table variant="unstyled" size="sm" marginBottom="30px">
          <Thead bg="none">
            <Tr color={colors.gray[600]}>
              <Th bg="none" w="40%">
                Funding source
              </Th>
              <Th bg="none" w="30%">
                Before
              </Th>
              <Th bg="none" w="30%">
                After
              </Th>
            </Tr>
          </Thead>
          <Tbody>{children}</Tbody>
        </Table>
        {((proposedAssignmentReceivables &&
          !!proposedAssignmentReceivables.length) ||
          (previouslyAssignedReceivables &&
            !!previouslyAssignedReceivables.length)) && (
          <EmailServicerReportPreviewButton
            creditFacilityId={creditFacilityId}
            previouslyAssignedReceivables={_.compact(
              previouslyAssignedReceivables,
            )}
            proposedReceivables={_.compact(proposedAssignmentReceivables)}
            fileVersion={fileVersion}
            reportingDate={reportingDate}
            buttonLabel="Servicer Report Preview"
            fileNamePrefix={proposedAndAssignedExtendedResponseTapePrefix}
          />
        )}
        {((proposedAssignmentReceivables &&
          !!proposedAssignmentReceivables.length) ||
          (previouslyAssignedReceivables &&
            !!previouslyAssignedReceivables.length)) && (
          <Accordion allowMultiple>
            <AccordionItem>
              <Text>
                <AccordionButton>
                  <Box as="span" flex="1" textAlign="left">
                    Download previews
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </Text>
              <AccordionPanel pb={4}>
                <Flex alignItems="center">
                  <Table sx={{ tableLayout: 'fixed' }}>
                    <Tbody>
                      {proposedAssignmentReceivables &&
                      !!proposedAssignmentReceivables.length ? (
                        <DownloadExtendedResponseTapePreviewButton
                          creditFacilityId={creditFacilityId}
                          proposedReceivables={_.compact(
                            proposedAssignmentReceivables,
                          )}
                          fileVersion={fileVersion}
                          reportingDate={reportingDate}
                          buttonLabel={proposedDownloadButtonLabel}
                          fileNamePrefix={
                            proposedExtendedResponseTapeFilePrefix
                          }
                        />
                      ) : (
                        <EmptyDownloadAssignmentPreviewButton
                          buttonLabel={proposedDownloadButtonLabel}
                        />
                      )}
                      {((proposedAssignmentReceivables &&
                        !!proposedAssignmentReceivables.length) ||
                        (previouslyAssignedReceivables &&
                          !!previouslyAssignedReceivables.length)) && (
                        <DownloadExtendedResponseTapePreviewButton
                          creditFacilityId={creditFacilityId}
                          previouslyAssignedReceivables={_.compact(
                            previouslyAssignedReceivables,
                          )}
                          proposedReceivables={_.compact(
                            proposedAssignmentReceivables,
                          )}
                          fileVersion={fileVersion}
                          reportingDate={reportingDate}
                          buttonLabel={proposedAndAssignedButtonLabel}
                          fileNamePrefix={
                            proposedAndAssignedExtendedResponseTapePrefix
                          }
                        />
                      )}
                    </Tbody>
                  </Table>
                </Flex>
              </AccordionPanel>
            </AccordionItem>
          </Accordion>
        )}
      </Box>
    </Box>
  );
};

const AssignmentSummaryBalanceSheetInfoBox = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => {
  return (
    <Box borderWidth="1px">
      <Box
        background={colors.tableHeader}
        w="100%"
        p={4}
        color="black"
        borderBottomWidth="1px"
      >
        {title}
      </Box>
      <Box margin="20px 0 20px 0">
        <Table variant="unstyled" size="sm">
          <Thead bg="none">
            <Tr color={colors.gray[600]}>
              <Th bg="none" w="40%">
                Funding source
              </Th>
              <Th bg="none" w="30%">
                Before
              </Th>
              <Th bg="none" w="30%">
                After
              </Th>
            </Tr>
          </Thead>
          <Tbody>{children}</Tbody>
        </Table>
      </Box>
    </Box>
  );
};

const AssignmentSummaryBorrowingBaseBox = ({
  creditFacilityId,
  initialConditionBorrowingBaseReport,
  latestBorrowingBaseReport,
  previouslyAssignedReceivables,
  proposedAssignmentReceivables,
  preview,
  fileVersion,
  reportingDate,
}: {
  creditFacilityId: string;
  initialConditionBorrowingBaseReport: AssignmentBorrowingBaseReportGQLType;
  latestBorrowingBaseReport: AssignmentBorrowingBaseReportGQLType;
  previouslyAssignedReceivables?: AssignmentReceivableGQLType[];
  proposedAssignmentReceivables?: AssignmentReceivableGQLType[];
  preview: boolean;
  fileVersion: number;
  reportingDate: Util.FullDateStr;
}) => {
  const {
    data: borrowerEntityNameData,
    loading: borrowerEntityNameLoading,
  } = useGetCreditAgreementBorrowerEntityNameQuery({
    variables: { creditFacilityId },
  });

  if (
    !borrowerEntityNameData ||
    !borrowerEntityNameData.creditAgreementBorrowerEntityName ||
    borrowerEntityNameLoading
  ) {
    return (
      <Skeleton
        data-testid="loading-assignment-summary-infobox"
        height="300px"
      />
    );
  }

  return (
    <>
      <AssignmentSummaryBorrowingBaseInfoBox
        title={borrowerEntityNameData.creditAgreementBorrowerEntityName}
        creditFacilityId={creditFacilityId}
        previouslyAssignedReceivables={previouslyAssignedReceivables}
        proposedAssignmentReceivables={proposedAssignmentReceivables}
        fileVersion={fileVersion}
        reportingDate={reportingDate}
      >
        <InfoBoxRow
          label="Exposure"
          before={numberToDollarFormattedRounded(
            initialConditionBorrowingBaseReport?.totalExposure || 0,
          )}
          after={numberToDollarFormattedRounded(
            latestBorrowingBaseReport?.totalExposure || 0,
          )}
          preview={preview}
        />
        <InfoBoxRow
          label="Borrowing base amount (receivables)"
          before={numberToDollarFormattedRounded(
            initialConditionBorrowingBaseReport?.totalBorrowingBaseAmount || 0,
          )}
          after={numberToDollarFormattedRounded(
            latestBorrowingBaseReport?.totalBorrowingBaseAmount || 0,
          )}
          preview={preview}
        />
        <InfoBoxRow
          label="Effective receivable advance rate"
          before={numberToPercentageLabel(
            initialConditionBorrowingBaseReport?.effectiveReceivableAdvanceRate ||
              0,
          )}
          after={numberToPercentageLabel(
            latestBorrowingBaseReport?.effectiveReceivableAdvanceRate || 0,
          )}
          preview={preview}
        />
      </AssignmentSummaryBorrowingBaseInfoBox>
    </>
  );
};

export const AssignmentSummaryBorrowingBaseBoxes = ({
  initialConditionBorrowingBaseReports,
  latestBorrowingBaseReports,
  previouslyAssignedReceivables,
  proposedAssignmentReceivables,
  creditFacilityIds,
  preview,
  fileVersion,
  reportingDate,
}: {
  initialConditionBorrowingBaseReports: AssignmentBorrowingBaseReportGQLType[];
  latestBorrowingBaseReports: AssignmentBorrowingBaseReportGQLType[];
  previouslyAssignedReceivables: AssignmentReceivableGQLType[];
  proposedAssignmentReceivables: AssignmentReceivableGQLType[];
  creditFacilityIds: string[];
  preview: boolean;
  fileVersion: number;
  reportingDate: Util.FullDateStr;
}) => {
  const previouslyAssignedReceivablesByCreditFacility = previouslyAssignedReceivables.length
    ? groupReceivablesByCreditFacilityId(previouslyAssignedReceivables)
    : undefined;
  const proposedAssignmentReceivablesByCreditFacility = proposedAssignmentReceivables.length
    ? groupReceivablesByCreditFacilityId(proposedAssignmentReceivables)
    : undefined;
  return (
    <Box>
      {_.map(creditFacilityIds, (creditFacilityId) => {
        const initialConditionBorrowingBaseReport = _.find(
          initialConditionBorrowingBaseReports,
          (report) => report.creditFacilityId === creditFacilityId,
        );
        const latestBorrowingBaseReport = _.find(
          latestBorrowingBaseReports,
          (report) => report.creditFacilityId === creditFacilityId,
        );

        finleyAssert(
          initialConditionBorrowingBaseReport && latestBorrowingBaseReport,
          new commonError.FinleyError(
            'initial condition and latest borrowing base aggregates must exist',
            {
              metadata: {
                logMeta: { creditFacilityId, fileVersion, reportingDate },
              },
            },
          ),
        );

        return (
          <AssignmentSummaryBorrowingBaseBox
            creditFacilityId={creditFacilityId}
            initialConditionBorrowingBaseReport={
              initialConditionBorrowingBaseReport
            }
            latestBorrowingBaseReport={latestBorrowingBaseReport}
            previouslyAssignedReceivables={
              previouslyAssignedReceivablesByCreditFacility
                ? previouslyAssignedReceivablesByCreditFacility[
                    creditFacilityId
                  ]
                : undefined
            }
            proposedAssignmentReceivables={
              proposedAssignmentReceivablesByCreditFacility
                ? proposedAssignmentReceivablesByCreditFacility[
                    creditFacilityId
                  ]
                : undefined
            }
            preview={preview}
            key={creditFacilityId}
            fileVersion={fileVersion}
            reportingDate={reportingDate}
          />
        );
      })}
    </Box>
  );
};

export const AssignmentSummaryBalanceSheetBox = ({
  initialConditionBalanceSheetExposure,
  latestBalanceSheetExposure,
  preview,
}: {
  initialConditionBalanceSheetExposure: number;
  latestBalanceSheetExposure: number;
  preview: boolean;
}) => {
  return (
    <AssignmentSummaryBalanceSheetInfoBox title="Parent balance Sheet">
      <InfoBoxRow
        label="Exposure"
        before={numberToDollarFormattedRounded(
          initialConditionBalanceSheetExposure,
        )}
        after={numberToDollarFormattedRounded(latestBalanceSheetExposure)}
        preview={preview}
      />
    </AssignmentSummaryBalanceSheetInfoBox>
  );
};

export const AssignmentSummaryInfoBoxes = ({
  initialConditionBorrowingBaseReports,
  latestBorrowingBaseReports,
  latestAssignmentReceivables,
  initialConditionAssignmentReceivables,
  creditFacilityIds,
  preview,
  fileVersion,
  reportingDate,
}: {
  initialConditionBorrowingBaseReports: AssignmentBorrowingBaseReportGQLType[];
  latestBorrowingBaseReports: AssignmentBorrowingBaseReportGQLType[];
  latestAssignmentReceivables?: AssignmentReceivableGQLType[];
  initialConditionAssignmentReceivables?: AssignmentReceivableGQLType[];
  creditFacilityIds: string[];
  preview: boolean;
  fileVersion: number;
  reportingDate: Util.FullDateStr;
}) => {
  // @dani assignment performance - remove when response tape preview download migrated
  const previouslyAssignedReceivables: AssignmentReceivableGQLType[] = [];
  const proposedAssignmentReceivables: AssignmentReceivableGQLType[] = [];
  const previouslyUnssignedReceivables: AssignmentReceivableGQLType[] = [];
  const proposedUnassignedReceivables: AssignmentReceivableGQLType[] = [];

  if (initialConditionAssignmentReceivables && latestAssignmentReceivables) {
    _.forEach(initialConditionAssignmentReceivables, (receivable) => {
      if (
        !receivable.isAssignable &&
        !!receivable.receivableResponseTapeProperties
      ) {
        previouslyAssignedReceivables.push(receivable);
      }

      if (receivable.isAssignable) {
        previouslyUnssignedReceivables.push(receivable);
      }
    });

    _.forEach(latestAssignmentReceivables, (receivable) => {
      if (
        receivable.isAssignable &&
        !!receivable.receivableResponseTapeProperties
      ) {
        proposedAssignmentReceivables.push(receivable);
      }

      if (
        receivable.isAssignable &&
        !receivable.receivableResponseTapeProperties
      ) {
        proposedUnassignedReceivables.push(receivable);
      }
    });
  }

  // balance sheet "exposure" is same for all the assignment borrowing base reports for one response tape
  const initialConditionBalanceSheetExposure =
    initialConditionBorrowingBaseReports[0].balanceSheetExposure;
  const latestBalanceSheetExposure =
    latestBorrowingBaseReports[0].balanceSheetExposure;
  return (
    <Box justifyContent="normal">
      <AssignmentSummaryBorrowingBaseBoxes
        creditFacilityIds={creditFacilityIds}
        initialConditionBorrowingBaseReports={
          initialConditionBorrowingBaseReports
        }
        latestBorrowingBaseReports={latestBorrowingBaseReports}
        previouslyAssignedReceivables={previouslyAssignedReceivables}
        proposedAssignmentReceivables={proposedAssignmentReceivables}
        preview={preview}
        fileVersion={fileVersion}
        reportingDate={reportingDate}
      />
      <AssignmentSummaryBalanceSheetBox
        initialConditionBalanceSheetExposure={
          initialConditionBalanceSheetExposure
        }
        latestBalanceSheetExposure={latestBalanceSheetExposure}
        preview={preview}
      />
    </Box>
  );
};

export const AssignmentSummaryDrawer = ({
  latestAssignmentReceivables,
  initialConditionAssignmentReceivables,
  latestBorrowingBaseReports,
  initialConditionBorrowingBaseReports,
  creditFacilityIds,
  fileVersion,
  reportingDate,
  preview,
  isOpen,
  onOpen,
  onClose,
}: {
  latestAssignmentReceivables?: AssignmentReceivableGQLType[];
  initialConditionAssignmentReceivables?: AssignmentReceivableGQLType[];
  initialConditionBorrowingBaseReports: AssignmentBorrowingBaseReportGQLType[];
  latestBorrowingBaseReports: AssignmentBorrowingBaseReportGQLType[];
  creditFacilityIds: string[];
  fileVersion: number;
  reportingDate: Util.FullDateStr;
  preview: boolean;
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
}) => {
  return (
    <Box>
      <IconButton
        ariaLabel="Check summary"
        label="Check summary"
        icon={<FiList />}
        onClick={() => onOpen()}
        variant="solid"
      />
      <Drawer onClose={onClose} isOpen={isOpen} placement="right" size="sm">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>Proposed assignments summary</DrawerHeader>
          <DrawerBody>
            <AssignmentSummaryInfoBoxes
              latestAssignmentReceivables={latestAssignmentReceivables}
              initialConditionAssignmentReceivables={
                initialConditionAssignmentReceivables
              }
              latestBorrowingBaseReports={latestBorrowingBaseReports}
              initialConditionBorrowingBaseReports={
                initialConditionBorrowingBaseReports
              }
              creditFacilityIds={creditFacilityIds}
              preview={preview}
              fileVersion={fileVersion}
              reportingDate={reportingDate}
            />
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Box>
  );
};
